import { ChevronRightIcon } from "@heroicons/react/outline";
import { format } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";

import { GetItemisedPurchases } from "../../../domain/usecases/get-itemised-purchases";
import { currencyFormatter } from "../../utils/helpers";
import { reportsState } from "./atoms/atoms";
import ItemisedAmountTotal from "./itemisedAmountTotal";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { currentAccountState } from "../../components";
import { db } from "../../../main/firebase";
import { createReportExcelFile } from "./excel/excel";

import {
  ProductProvider,
  PurchasesOrderItem,
  SuccessfulPurchase,
} from "../../../domain/usecases/get-account-balances";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Loading from "../../components/loading/loading";
import ItemisedPurchasesElectricityRow from "./itemisedPurchasesElectricityRow";
import ItemisedPurchasesAirtimeRow from "./itemisedPurchasesAirtimeRow";
import ItemisedPurchasesDSTVRow from "./itemisedPurchasesDSTVRow";
import ItemisedPurchasesFailureElectricityTable from "./itemisedPurchasesFailureTable";
import ItemisedPurchasesFailureTable from "./itemisedPurchasesFailureTable";
import ExportToExcel from "./exportExcel";
import { moneyFormat } from "../../utils/moneyFormat";

export default function ItemisedPurchases() {
  const [state, setState] = useRecoilState(reportsState);
  const { getCurrentAccount } = useRecoilValue(currentAccountState);
  const [nameFilter, setNameFilter] = useState(null);
  const [numberFilter, setNumberFilter] = useState(null);
  const [companyFilter, setCompanyFilter] = useState(null);

  const [failureItem, setFailureMeter] = useState(null);
  const [failureTransaction, setFailureTransaction] = useState(null);

  useEffect(() => {
    setState((old) => ({
      ...old,
      isLoading: true,
    }));
    const q = query(
      collection(db, "purchaseOrderLineItem"),
      where("account", "==", getCurrentAccount().account),
      where("dateCreated", ">=", state.startDate.startOf("day").toDate()),
      where("dateCreated", "<=", state.endDate.endOf("day").toDate())
    );

    return onSnapshot(q, (querySnapshot) => {
      let itemisedPurchases = querySnapshot.docs
        .sort(
          (a, b) =>
            a.data().dateUpdated.toDate() - b.data().dateUpdated.toDate()
        )
        .map((doc) => {
          return {
            account: doc.data().account,
            accountName: doc.data().accountName,
            amount: doc.data().amount,
            dateCreated: doc.data().dateCreated.toDate(),
            dateUpdated: doc.data().dateUpdated.toDate(),
            id: doc.data().id,
            // purchaseOrderId: doc.data().purchaseOrderId,
            number: doc.data().itemNumber,
            createdByName: doc.data().createdBy,
            createdBy: doc.data().createdBy,
            approvedBy: doc.data().approvedBy,
            location: doc.data().location ? doc.data().location : null,
            name: doc.data().name ? doc.data().name : null,
            owner: doc.data().registeredOwner
              ? doc.data().registeredOwner
              : doc.data().owner,
            merchant: doc.data().merchant,
            itemType: doc.data().itemType,
            purchaseAmount: doc.data().purchaseAmount,
            lastPurchaseDate: doc.data().lastPurchaseDate,
            lastPurchaseAmount: doc.data().lastPurchaseAmount,
            jobTitle: doc.data().jobTitle ? doc.data().jobTitle : null,
            network: doc.data().network ? doc.data().network : null,
            company: {
              name: doc.data().company.name,
              companyNumber: doc.data().company.companyNumber,
              phoneNumber: doc.data().company.phoneNumber,
              postalAddress: doc.data().company.postalAddress,
              city: doc.data().company.city,
            },
            purchaseOrderNo: doc.data().purchaseOrderNo,
            purchaseOrderReference: doc.data().purchaseOrderReference,
            registeredOwner: doc.data().registeredOwner,
            response: doc.data().response,
            transactionId: doc.data().transactionId,
            status: doc.data().status,
          } as PurchasesOrderItem;
        });

      setState((old) => ({
        ...old,
        isLoading: false,
        itemisedPurchases: itemisedPurchases,
      }));
    });
  }, [state.startDate, state.endDate]);

  useEffect(() => {
    const q = query(collection(db, "providers"));

    return onSnapshot(q, (querySnapshot) => {
      let providers = querySnapshot.docs.map((doc) => {
        return {
          acronym: doc.data().acronym,
          city: doc.data().city,
          name: doc.data().name,
          postalAddress: doc.data().postalAddress,
          product: doc.data().product,
          telephone: doc.data().telephone,
          companyNumber: doc.data().companyNumber
            ? doc.data().companyNumber
            : "None",
          callCentre: doc.data().callCentre ? doc.data().callCentre : "None",
          vatNumber: doc.data().vatNumber ? doc.data().vatNumber : "None",
        } as ProductProvider;
      });

      setState((old) => ({
        ...old,
        providers: providers,
      }));
    });
  }, []);

  const handleMeterNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    const keyword = e.target.value.toLowerCase();
    const isKeyword = keyword.length > 0 ? true : false;
    const isFiltered = nameFilter ? true : false;

    // keyword.length ? setNumberFilter(keyword) : setNumberFilter(null);

    isKeyword ? setNumberFilter(keyword) : setNumberFilter(null);

    let filtered: PurchasesOrderItem[];

    if (isFiltered && isKeyword) {
      filtered = state.itemisedPurchases.filter((item) => {
        return (
          item.number.toLowerCase().includes(keyword) &&
          (item.name
            ? item.name.toLowerCase().includes(nameFilter)
            : item.owner.toLowerCase().includes(nameFilter))
        );
      });
    } else if (!isKeyword && isFiltered) {
      filtered = state.itemisedPurchases.filter((item) => {
        return item.name
          ? item.name.toLowerCase().includes(nameFilter)
          : item.owner.toLowerCase().includes(nameFilter);
      });
    } else if (isKeyword && !isFiltered) {
      filtered = state.itemisedPurchases.filter((item) => {
        return item.number.toLowerCase().includes(keyword);
      });
    } else {
      filtered = [];
    }

    let updateMetersState = {
      ...state,
      filteredItemisedPurchases: filtered.length > 0 ? filtered : [],
      isFiltered: isFiltered || isKeyword ? true : false,
    };

    setState(updateMetersState);
  };

  const handleMeterNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    const keyword = e.target.value.toLowerCase();
    const isKeyword = keyword.length > 0 ? true : false;
    const isFiltered = numberFilter ? true : false;

    // keyword.length ? setNumberFilter(keyword) : setNumberFilter(null);

    console.log("keyword", keyword);
    console.log("state.itemisedPurchases", state.itemisedPurchases);
    isKeyword ? setNameFilter(keyword) : setNameFilter(null);

    let filtered: PurchasesOrderItem[];

    if (isFiltered && isKeyword) {
      filtered = state.itemisedPurchases.filter((item) => {
        return (
          (item.name
            ? item.name.toLowerCase().includes(keyword)
            : item.owner.toLowerCase().includes(keyword)) &&
          item.number.toLowerCase().includes(numberFilter)
        );
      });
    } else if (!isKeyword && isFiltered) {
      filtered = state.itemisedPurchases.filter((item) => {
        return item.number.toLowerCase().includes(numberFilter);
      });
    } else if (isKeyword && !isFiltered) {
      filtered = state.itemisedPurchases.filter((item) => {
        return item.name
          ? item.name.toLowerCase().includes(keyword)
          : item.owner.toLowerCase().includes(keyword);
      });
    } else {
      filtered = [];
    }

    let updateMetersState = {
      ...state,
      filteredItemisedPurchases: filtered.length > 0 ? filtered : [],
      isFiltered: isFiltered || isKeyword ? true : false,
    };

    setState(updateMetersState);
  };

  const handleCompanyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    const keyword = e.target.value.toLowerCase();
    const isKeyword = keyword.length > 0 ? true : false;
    const isFiltered = companyFilter ? true : false;

    // keyword.length ? setNumberFilter(keyword) : setNumberFilter(null);

    isKeyword ? setNameFilter(keyword) : setNameFilter(null);

    let filtered: PurchasesOrderItem[];

    if (isKeyword) {
      filtered = state.itemisedPurchases.filter((item) => {
        return item.company.name.toLowerCase().includes(keyword);
      });
    } else {
      filtered = [];
    }

    let updateMetersState = {
      ...state,
      filteredItemisedPurchases: filtered.length > 0 ? filtered : [],
      isFiltered: isFiltered || isKeyword ? true : false,
    };

    setState(updateMetersState);
  };

  const itemisedPurchasesList = state.isFiltered
    ? state.filteredItemisedPurchases.length > 0
      ? state.filteredItemisedPurchases
      : []
    : state.itemisedPurchases;

  const printFailureTable = useCallback(() => {
    // setFailureMeter(item);

    if (failureItem === null) {
      return;
    }

    const doc = new jsPDF();

    var imgData =
      "data:image/png;base64,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";

    doc.setFontSize(40);

    doc.addImage(imgData, "PNG", 14, 20, 38.25, 5.4);
    // doc.output("datauri");

    doc.setFontSize(22);
    doc.text(`Failed Transaction`, 56, 25.5);
    doc.setTextColor(100);

    doc.setFontSize(12);
    doc.text(`Purchase Number - ${failureTransaction.purchaseOrderId}`, 14, 44);
    doc.setTextColor(100);

    autoTable(doc, {
      theme: "plain",
      headStyles: { fontSize: 10, fontStyle: "bold" },
      bodyStyles: { fontSize: 10, fontStyle: "normal" },
      styles: { cellPadding: 0.5 },
      startY: 48,
      head: [
        [
          "Order Reference",
          "Submitted By",
          "Approved By",
          "Date of Submission",
        ],
      ],
      body: [
        [
          `${failureTransaction.purchaseOrderReference}`,
          `${failureTransaction.createdBy}`,
          `${failureTransaction.approvedBy}`,
          `${format(failureTransaction.dateUpdated, "yyyy-MM-dd HH:mm:ss")}`,
          //  {format(failureTransaction.dateUpdated.toDate(),"yyyy-MM-dd HH:mm:ss" ),
        ],
      ],
    });

    doc.line(195.5, 66, 14, 66);

    autoTable(doc, { html: "#failure-table", startY: 76 });

    doc.save(
      `${failureTransaction.purchaseOrderReference}-${failureTransaction.number}.pdf`
    );
  }, [failureTransaction, failureItem]);

  let generateSalesTotalsCell = (worksheet, columnLetter, totalDataRows) => {
    const firstDataRow = 11;
    const lastDataRow = firstDataRow + totalDataRows - 1;

    const firstCellReference = `${columnLetter}${firstDataRow}`;
    const lastCellReference = `${columnLetter}${lastDataRow}`;
    const sumRange = `${firstCellReference}:${lastCellReference}`;

    return {
      formula: `SUM(${sumRange})`,
    };
  };

  const transactionsSheet = async (
    transactions,
    workbook,
    logo,
    reportStartDate,
    reportEndDate,
    merchantName
  ) => {
    let startDate = format(reportStartDate.toDate(), "yyyy-MM-dd");
    let endDate = format(reportEndDate.toDate(), "yyyy-MM-dd");
    console.log("transactions!!!!!!!", transactions);
    let worksheet = workbook.addWorksheet("All Sales", {
      properties: {
        showGridLines: false,
      },
      pageSetup: {
        paperSize: 9,
        fitToPage: true,
        orientation: "portrait",
      },
      // views: [
      //   {
      //     state: "frozen",
      //     xSplit: 9,
      //     activeCell: "G1",
      //     showGridLines: false,
      //   },
      // ],
    });

    worksheet.mergeCells("A1", "D2");
    worksheet.addImage(logo, {
      tl: {
        col: 0.25,
        row: 0.25,
      },
      ext: {
        width: 150,
        height: 32,
      },
    });

    worksheet.mergeCells("A3", "I3");

    worksheet.mergeCells("A4", "D4");
    worksheet.getCell("A4").value = {
      richText: [
        {
          font: {
            size: 12,
            color: {
              argb: "FF000000",
            },
            name: "Calibri",
            family: 4,
            scheme: "minor",
          },
          text: "For ",
        },
        {
          font: {
            size: 12,
            color: {
              argb: "FF000000",
            },
            bold: true,
            name: "Calibri",
            family: 4,
            scheme: "minor",
          },
          text: `${merchantName}`,
        },
      ],
    };

    worksheet.mergeCells("A5", "I5");

    worksheet.mergeCells("A6", "D6");
    worksheet.getCell("A6").value = `Transactions For Period`;
    worksheet.getRow(6).font = {
      name: "Calibri",
      family: 4,
      size: 16,
      bold: true,
    };

    worksheet.mergeCells("A7", "I7");

    worksheet.mergeCells("A8", "D8");
    worksheet.getCell("A8").value = {
      richText: [
        {
          font: {
            size: 12,
            color: {
              argb: "FF000000",
            },
            name: "Calibri",
            family: 4,
            scheme: "minor",
          },
          text: "Date From ",
        },
        {
          font: {
            size: 12,
            color: {
              argb: "FF000000",
            },
            bold: true,
            name: "Calibri",
            family: 4,
            scheme: "minor",
          },
          text: `${startDate}`,
        },
        {
          font: {
            size: 12,
            color: {
              argb: "FF000000",
            },
            name: "Calibri",
            family: 4,
            scheme: "minor",
          },
          text: " to ",
        },
        {
          font: {
            size: 12,
            color: {
              argb: "FF000000",
            },
            bold: true,
            name: "Calibri",
            family: 4,
            scheme: "minor",
          },
          text: `${endDate}`,
        },
      ],
    };

    worksheet.mergeCells("A9", "J9");

    // adjust pageSetup settings afterwards
    worksheet.pageSetup.margins = {
      left: 0.7,
      right: 0.7,
      top: 0.75,
      bottom: 0.75,
      header: 0.3,
      footer: 0.3,
    };

    let headers = worksheet.getRow(10);
    headers.height = 25;
    headers.values = [
      "Date Time",
      "Company",
      "Type",
      "Name",
      "Number",
      "Units (kWh)",
      "Amount",
      "Created By",
      "Approved By",
      "Status",
    ];
    headers.alignment = {
      vertical: "middle",
      horizontal: "left",
    };

    headers.eachCell((cell) => {
      cell.font = {
        name: "Calibri",
        family: 4,
        size: 10,
        color: {
          argb: "FFFFFFFF",
        },
        bold: true,
      };
      cell.border = {
        bottom: {
          style: "thick",
        },
      };
    });

    worksheet.columns = [
      {
        key: "dateTime",
        width: 20,
      },
      {
        key: "company",
        width: 24,
      },
      {
        key: "itemType",
        width: 18,
      },
      {
        key: "name",
        width: 24,
      },
      {
        key: "number",
        width: 18,
      },
      {
        key: "numUnits",
        width: 18,
      },
      {
        key: "amount",
        width: 16,
      },
      {
        key: "createdBy",
        width: 24,
      },
      {
        key: "approvedBy",
        width: 24,
      },
      {
        key: "status",
        width: 16,
      },
    ];

    // Column formats: G (Amount) as currency-like numeric, F (Units) as numeric
    worksheet.getColumn(5).numFmt = "0.000";
    worksheet.getColumn(7).numFmt = "0.00";

    ["A10", "B10", "C10", "D10", "E10", "F10", "G10", "H10", "I10", "J10"].map(
      (key) => {
        worksheet.getCell(key).fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: {
            argb: "FF002060",
          },
        };
      }
    );

    transactions.forEach((doc, index) => {
      worksheet
        .addRow({
          dateTime: format(doc.dateUpdated, "yyyy-MM-dd HH:mm:ss"),
          company: doc.company.name,
          itemType: doc.itemType,
          name: doc.name,
          number: doc.number,
          // tokenUnits sometimes ends with unit suffix; ensure numeric
          numUnits: (() => {
            const unitsRaw = doc?.response?.creditVendReceipt?.tokenUnits;
            if (!unitsRaw) return 0;
            const stripped = typeof unitsRaw === "string" ? unitsRaw.slice(0, -3) : unitsRaw;
            return Number(stripped) 
          })(),
          // keep amount numeric so Excel can SUM
          amount: Number.isFinite(Number(doc.amount)) ? Number(doc.amount) : 0,
          createdBy: doc.createdBy,
          approvedBy: doc.approvedBy,
          status: doc.status,
        })
        .commit();
      let rowNum = index + 11;
      let row = worksheet.getRow(rowNum);
      row.outlineLevel = 2;
      // row.alignment = {
      //     vertical: "middle",
      //     horizontal: "center"
      // };
    });

    // Object
    // account: 2981
    // accountName: "ElliotCosmas"
    // amount: 80
    // approvedBy: "Elliot Cosmas"
    // company: {name: 'Verdure', companyNumber: '123', phoneNumber: '123', postalAddress: '1213', city: '1313'}
    // createdBy: "Elliot Cosmas"
    // createdByName: "Elliot Cosmas"
    // dateCreated: Wed May 11 2022 14:27:41 GMT+0200 (Central Africa Time) {}
    // dateUpdated: Wed May 11 2022 15:49:54 GMT+0200 (Central Africa Time) {}
    // id: "beaaad28-d0ef-42f4-a28c-d7645cd684bc"
    // itemType: "ELECTRICITY"
    // jobTitle: null
    // lastPurchaseAmount: 0
    // lastPurchaseDate: "None"
    // location: "Maun"
    // merchant: undefined
    // name: "Test 4"
    // network: null
    // number: "04040404040"
    // owner: "GABORONE CITY COUNCIL"
    // purchaseAmount: undefined
    // purchaseOrderNo: "2981-30"
    // purchaseOrderReference: "45"
    // registeredOwner: undefined
    // response: {clientSaleId: '2981-1652276990406', code: '0', creditVendReceipt: {…}, response: 'Successful', description: 'A mandatory field(outletId) is missing or in an invalid format', …}
    // status: "SUCCESSFUL"
    // transactionId: undefined
    // [[Prototype]]: Object

    let column7 = worksheet.getColumn(7);
    column7.alignment = {
      vertical: "middle",
      horizontal: "right",
    };
    column7.numFmt = "0.00";
    column7.width = 15;

    // Use SUMIF to exclude FAILED statuses (Amount column G, Status column J)
    const firstDataRow = 11;
    const lastDataRow = firstDataRow + transactions.length - 1;
    const amountRange = `G${firstDataRow}:G${lastDataRow}`;
    const statusRange = `J${firstDataRow}:J${lastDataRow}`;
    const total = transactions.length > 0
      ? { formula: `SUMIF(${statusRange},"<>FAILED",${amountRange})` }
      : 0;

    const totalsRow = worksheet.addRow([
      "",
      "",
      "",
      "",
      "",
      "Sales Totals",
      total,
      "",
      "",
      "",
    ]);

    totalsRow.height = 25;
    totalsRow.alignment = {
      vertical: "middle",
      horizontal: "right",
    };

    totalsRow.eachCell((cell) => {
      cell.font = {
        name: "Calibri",
        family: 4,
        size: 10,
        color: {
          argb: "FFFFFFFF",
        },
        bold: true,
      };

      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: {
          argb: "FF002060",
        },
      };
    });
  };

  const exportToExcel = async () => {
    // openModal("SpinnerModal");

    // console.log("clicked on exportToExcel", itemisedPurchasesList);

    await createReportExcelFile(
      "Itemised Report",
      itemisedPurchasesList,
      transactionsSheet,
      itemisedPurchasesList[0].accountName
        ? itemisedPurchasesList[0].accountName
        : "",
      state.startDate,
      state.endDate
    );
  };

  useEffect(() => {
    printFailureTable(); // This is be executed when `loading` state changes
  }, [failureTransaction, failureItem]);

  return (
    <>
      {itemisedPurchasesList && (
        <div className="w-full flex flex-col lg:max-w-7xl bg-white overflow-hidden shadow rounded-sm">
          <div className="px-6 py-4">
            <div className="mb-0">
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-12">
                <div className="sm:col-span-1 my-auto align-middle">
                  <h2 className="mx-auto text-lg leading-6 font-medium text-gray-900">
                    Filter:
                  </h2>
                </div>

                <div className="sm:col-span-2">
                  <div className=" border-b border-gray-300 focus-within:border-blue-600">
                    <input
                      onChange={(e) => handleCompanyChange(e)}
                      type="text"
                      name="company"
                      id="company"
                      className="block w-full border-0  py-2 border-b border-transparent bg-gray-50 focus:border-blue-600 focus:ring-0 sm:text-sm"
                      placeholder="Company"
                    />
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <div className=" border-b border-gray-300 focus-within:border-blue-600">
                    <input
                      onChange={(e) => handleMeterNameChange(e)}
                      type="text"
                      name="name"
                      id="name"
                      className="block w-full border-0 py-2 border-b border-transparent bg-gray-50 focus:border-blue-600 focus:ring-0 sm:text-sm"
                      placeholder="Name"
                    />
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <div className=" border-b border-gray-300 focus-within:border-blue-600">
                    <input
                      onChange={(e) => handleMeterNumberChange(e)}
                      type="text"
                      name="number"
                      id="number"
                      className="block w-full border-0  py-2 border-b border-transparent bg-gray-50 focus:border-blue-600 focus:ring-0 sm:text-sm"
                      placeholder="Number"
                    />
                  </div>
                </div>

                <div className="lg:col-span-5 flex justify-end">
                  {state.itemisedPurchases.length > 0 && (
                    <button
                      onClick={() => exportToExcel()}
                      type="button"
                      className="pr-3 bg-white"
                    >
                      <ExportToExcel />
                    </button>
                  )}

                  <ItemisedAmountTotal transactions={itemisedPurchasesList} />
                </div>
              </div>
            </div>
          </div>
          <div className="px-6 pb-6 flex  flex-grow">
            <div className="shadow sm:hidden">
              <ul className="mt-2 divide-y divide-gray-200 overflow-hidden shadow sm:hidden">
                {itemisedPurchasesList.map((transaction) => (
                  <li key={transaction.id}>
                    <a
                      href={transaction.id}
                      className="block px-4 py-4 bg-white hover:bg-gray-50"
                    >
                      <span className="flex items-center space-x-4">
                        <span className="flex-1 flex space-x-2 truncate">
                          <span className="flex flex-col text-gray-500 text-sm truncate">
                            <span className="truncate">{transaction.name}</span>
                            <span>
                              <span className="text-gray-900 font-medium">
                                {currencyFormatter(transaction.amount)}
                              </span>
                            </span>

                            {format(
                              new Date(transaction.dateUpdated),
                              "yyyy-MM-dd HH:mm:ss"
                            )}
                          </span>
                        </span>
                        <ChevronRightIcon
                          className="flex-shrink-0 h-5 w-5 text-gray-400"
                          aria-hidden="true"
                        />
                      </span>
                    </a>
                  </li>
                ))}
              </ul>

              <nav
                className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200"
                aria-label="Pagination"
              >
                <div className="flex-1 flex justify-between">
                  <a
                    href="/"
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:text-gray-500"
                  >
                    Previous
                  </a>
                  <a
                    href="/"
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:text-gray-500"
                  >
                    Next
                  </a>
                </div>
              </nav>
            </div>

            {/* Activity table (small breakpoint and up) */}
            <div className=" flex-grow flex hidden sm:block">
              <div className="flex-grow max-w-7xl mx-auto">
                <div className="flex flex-col mt-2">
                  <div className="align-middle min-w-full flex-grow overflow-x-auto overflow-hidden ">
                    {itemisedPurchasesList.length > 0 ? (
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead>
                          <tr>
                            <th className="pr-6 py-3  text-left text-sm font-medium text-gray-500  tracking-wider">
                              Date
                            </th>
                            <th className="px-6 py-3  text-left text-sm font-medium text-gray-500  tracking-wider">
                              Company
                            </th>
                            <th className="px-6 py-3 text-left text-sm font-medium text-gray-500  tracking-wider">
                              Type
                            </th>
                            <th className="px-6 py-3 text-left text-sm font-medium text-gray-500  tracking-wider">
                              Name
                            </th>
                            <th className="px-6 py-3 text-left text-sm font-medium text-gray-500  tracking-wider">
                              Number
                            </th>
                            <th className="px-6 py-3 text-right text-sm font-medium text-gray-500  tracking-wider">
                              Amount
                            </th>
                            <th className="hidden px-6 py-3  text-left text-sm font-medium text-gray-500  tracking-wider md:block">
                              Status
                            </th>
                            <th className="px-2 py-2  text-left text-sm font-medium text-gray-500  tracking-wider"></th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                          {itemisedPurchasesList.map((transaction) => {
                            if (transaction.itemType === "ELECTRICITY")
                              return (
                                <ItemisedPurchasesElectricityRow
                                  transaction={transaction}
                                />
                              );

                            if (transaction.itemType === "AIRTIME")
                              return (
                                <ItemisedPurchasesAirtimeRow
                                  transaction={transaction}
                                />
                              );

                            if (transaction.itemType === "DSTV")
                              return (
                                <ItemisedPurchasesDSTVRow
                                  transaction={transaction}
                                />
                              );
                          })}
                        </tbody>
                      </table>
                    ) : (
                      <h2 className="text-md leading-6 font-medium text-gray-900">
                        No results found for the selected dates/filters.
                      </h2>
                    )}

                    {failureItem && (
                      <ItemisedPurchasesFailureTable
                        transaction={failureItem}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {state.isLoading && <Loading />}
    </>
  );
}
